import has from 'lodash/get'
import { graphql } from 'react-apollo'
import { ApplicationError, NotFoundError } from '../../helpers/errors'
import moduleForDisplayMode from '../../helpers/module-for-display-mode'
import {
  ENTRY_TEMPLATES,
  TEMPLATE_WHITELIST,
  CENTRE_TEMPLATE,
  CENTRE_SERVICES,
} from '../../helpers/templates'
import deepCopy from '../../helpers/deep-copy'
import { buildRouteFromPath } from '../../helpers/route-helper'
import query from './query.graphql'

const isCollection = module =>
  module.sys.contentTypeId === 'manualCollection' ||
  module.sys.contentTypeId === 'automatedCollection'

const isRemoteCollection = module =>
  module.sys.contentTypeId === 'centreReviews' ||
  module.sys.contentTypeId === 'socialFeed'

const isUtilityPanel = module => module.sys.contentTypeId === 'utilityPanel'

const collectionHasNoEntries = module =>
  module.entries && module.entries.length === 0

const remoteCollectionHasNoEntries = module =>
  (has(module, 'reviewData.entries') &&
    module.reviewData.entries.length === 0) ||
  (has(module, 'socialFeedData.entries') &&
    module.socialFeedData.entries.length === 0)

const isCentreHome = templatePath => templatePath === CENTRE_TEMPLATE
const isCentreServices = templatePath => templatePath === CENTRE_SERVICES
const useModuleToCreateFirstFeature = (module, ownProps) => {
  if (module.entries.length === 1) {
    return [
      moduleForDisplayMode(ownProps.centre, {
        ...module,
        displayMode: 'Feature',
      }),
    ]
  }

  let featureModule = deepCopy(module)
  let collectionModule = deepCopy(module)
  const [first, ...remaining] = module.entries

  featureModule = {
    ...featureModule,
    entries: [first],
    displayMode: 'Feature',
  }

  collectionModule = {
    ...collectionModule,
    entries: remaining,
  }
  return [
    moduleForDisplayMode(ownProps.centre, featureModule),
    moduleForDisplayMode(ownProps.centre, collectionModule),
  ]
}
const mapPropsToOptions = ({ templatePath, centre, id }) => ({
  errorPolicy: 'ignore',
  variables: {
    path: templatePath,
    centreId: centre.sys.id,
    ignoreEntriesWithIds: (id && [id]) || [],
  },
})

export const mapDataToProps = ({
  data: { error, loading, pageTemplate },
  ownProps,
}) => {
  if (error) {
    return {
      error: new ApplicationError(error),
    }
  } else if (loading) {
    return { loading }
  } else if (!pageTemplate) {
    const templatePath =
      typeof ownProps.templatePath === 'string'
        ? ownProps.templatePath.toLowerCase()
        : ownProps.templatePath

    // The template is not valid
    // If this should be redirected return redirect
    const redirectData = TEMPLATE_WHITELIST.find(
      ({ template }) => template === templatePath
    )

    if (redirectData) {
      const newPath = buildRouteFromPath(redirectData.redirect, {
        centre: ownProps.centre.slug,
      })
      return {
        redirect: newPath,
      }
    }
    // If this is for a known entry don't set error - return empty modules
    if (ENTRY_TEMPLATES.includes(ownProps.templatePath)) {
      return {
        ...ownProps,
        modules: [],
      }
    }

    return {
      error: new NotFoundError(),
    }
  }

  const pageTemplateModules = pageTemplate.modules
    .reduce((modules, module) => {
      // The API can return a collection with no entries. Let's filter them out.
      if (isCollection(module) && collectionHasNoEntries(module)) {
        return modules
      }

      if (isRemoteCollection(module) && remoteCollectionHasNoEntries(module)) {
        return modules
      }

      // The first module of a Centre Home page should be a Feature
      if (isCentreHome(ownProps.templatePath)) {
        if (
          module.displayMode === 'Feature' ||
          isRemoteCollection(module) ||
          isUtilityPanel(module)
        ) {
          return [...modules, moduleForDisplayMode(ownProps.centre, module)]
        }

        if (modules.length === 0 && isCollection(module)) {
          return useModuleToCreateFirstFeature(module, ownProps)
        } else if (modules.length === 0) {
          return [...modules, moduleForDisplayMode(ownProps.centre, module)]
        }

        if (modules[0].displayMode !== 'Feature') {
          const newModules = useModuleToCreateFirstFeature(module, ownProps)
          if (newModules.length === 1) {
            return [newModules[0], ...modules]
          }
          return [newModules[0], ...modules, newModules[1]]
        }
      }
      if (isCentreServices(ownProps.templatePath)) {
        return [
          ...modules,
          moduleForDisplayMode(ownProps.centre, {
            ...module,
            templatePath: CENTRE_SERVICES,
          }),
        ]
      }

      return [...modules, moduleForDisplayMode(ownProps.centre, module)]
    }, [])
    .filter(mod => mod) // moduleForDisplayMode returns null for unknown modules so remove them
  return {
    ...ownProps,
    template: pageTemplate,
    modules: pageTemplateModules,
  }
}

const withModules = graphql(query, {
  options: mapPropsToOptions,
  props: mapDataToProps,
})

export default withModules
